import React, {useState} from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import {GlgPopup, FaIcon, GlgTooltip} from 'SharedComponents'
import ScheduleRounds from '../shared/components/schedule_rounds/schedule_rounds'
import {GGButton} from '../shared/new_components/GGButton'
import {initialWeekDays} from '../shared/components/schedule_rounds/week_days'

import {callAPI, formatDate, getAuthenticityToken, showErrorNotification} from '../shared/helpers'

// On the GolfHub setup page the round type defaults to the event's product:
// 'league' events generate weekly rounds, everything else adds rounds manually.
const roundTypeForProduct = (product) => (product === 'league' ? 'weekly' : 'manually')

// Persisted rounds tag their Date with the round id so the popup can tell
// same-date rounds apart; dates picked inside the popup stay untagged and
// read as rounds to create.
const parseRounds = (rounds) => rounds.map(r => {
  const date = moment(r.date, 'MM/DD/YYYY').toDate()
  date.roundId = r.id
  return {
    id: r.id,
    date,
    deletable: r.deletable,
  }
})

const RoundSchedulerRoot = ({
  massCreateUrl,
  removeRoundUrl,
  getRoundsUrl,
  existingRounds,
  leagueProduct,
  golfhubPageCreated,
  customerDateFormat,
  dateRequired,
}) => {
  const parsedRounds = parseRounds(existingRounds)

  const [ showPopup, setShowPopup ] = useState(false)
  const [ confirmedRounds, setConfirmedRounds ] = useState(parsedRounds)
  const [ details, setDetails ] = useState({
    actionType: 'createEvent',
    rounds: parsedRounds.map(r => r.date),
    manuallyRounds: parsedRounds.map(r => r.date),
    weeklyRounds: [],
  })
  const [ dayRoundNumber, setDayRoundNumber ] = useState({})
  const [ givenDays, setGivenDays ] = useState([])
  const [ startDate, setStartDate ] = useState('')
  const [ numberOfWeeks, setNumberOfWeeks ] = useState('')
  const [ selectedButton, setSelectedButton ] = useState(roundTypeForProduct(leagueProduct))
  const [ defaultRoundType, setDefaultRoundType ] = useState(roundTypeForProduct(leagueProduct))
  const [ selectedWeekDays, setSelectedWeekDays ] = useState(initialWeekDays())
  const [ requestPending, setRequestPending ] = useState(false)
  // Id of the round whose chip-delete job is running - drives the per-chip
  // spinner while the whole widget is pending
  const [ removingRoundId, setRemovingRoundId ] = useState(null)

  // Reads the currently selected product on the page (GG Golf Hub can toggle it via
  // the league_product chip group); falls back to the server-provided value.
  const currentLeagueProduct = () => {
    const checked = document.querySelector('input[name="league_product"]:checked')
    return checked ? checked.value : leagueProduct
  }

  const handleOpen = () => {
    const roundType = roundTypeForProduct(currentLeagueProduct())
    setDefaultRoundType(roundType)
    setSelectedButton(roundType)
    // The weekly-generator inputs live in this root, so they survive popup
    // closes. The popup children remount on every open and AddWeeklyRounds
    // regenerates its series from these inputs on mount - left stale, that
    // resurrects rounds deleted since the last save (and duplicates kept
    // ones), so start every session from a clean generator.
    setGivenDays([])
    setStartDate('')
    setNumberOfWeeks('')
    setSelectedWeekDays(initialWeekDays())
    setDetails({
      actionType: 'createEvent',
      rounds: confirmedRounds.map(r => r.date),
      manuallyRounds: confirmedRounds.map(r => r.date),
      weeklyRounds: [],
    })
    setShowPopup(true)
  }

  // Large batches (6+ dates on a league with members) run in a background job.
  // Poll it until it reports completed so the popup never redirects to the job
  // page; polling also kick-starts a job that is still in "scheduled" state.
  const JOB_POLL_INTERVAL = 2000
  const JOB_POLL_TIMEOUT = 300000

  const finishJobPolling = (onDone) => {
    setRequestPending(false)
    setRemovingRoundId(null)
    if (onDone) {
      onDone()
    }
  }

  const pollRoundsJob = (jobId, onDone, startedAt) => {
    const retry = () => setTimeout(() => pollRoundsJob(jobId, onDone, startedAt), JOB_POLL_INTERVAL)

    if (Date.now() - startedAt > JOB_POLL_TIMEOUT) {
      showErrorNotification(window.I18n.t('shared.components.schedule_rounds.processing_rounds'))
      finishJobPolling(onDone)
      return
    }

    // A "scheduled" job is only moved into the queue when its poll endpoint is
    // hit (normally done by the job page) - keep pinging it, ignore the body
    fetch(`/jobs/${jobId}/poll.json`, {credentials: 'same-origin'}).catch(() => {})

    callAPI(`${getRoundsUrl}?job_id=${jobId}`, 'GET')
      .then(data => {
        if (data.job_status === 'completed') {
          finishJobPolling(onDone)
        } else if (data.job_status === 'error') {
          showErrorNotification(window.I18n.t('views.golfhub.request_failed'))
          finishJobPolling(null)
        } else {
          retry()
        }
      })
      .catch(retry)
  }

  // A round whose id-tagged Date survived in the kept list was kept - exact
  // match, no guessing between same-date rounds. Untagged kept dates (picked
  // in the popup, or regenerated by the weekly flow which builds plain Dates)
  // fall back to date matching with multiplicity, locked (scored) rounds
  // consuming their date's slots first. Whatever is left and deletable was
  // removed by the user.
  const computeRemovedRoundIds = (keptDates) => {
    const keptIds = new Set()
    const keptCounts = {}
    keptDates.forEach(date => {
      if (date.roundId) {
        keptIds.add(date.roundId)
      } else {
        const key = +date
        keptCounts[key] = (keptCounts[key] || 0) + 1
      }
    })

    const lockedFirst = [ ...confirmedRounds ].sort((a, b) => Number(a.deletable) - Number(b.deletable))
    return lockedFirst
      .filter(round => {
        if (keptIds.has(round.id)) {
          return false
        }
        const key = +round.date
        if (keptCounts[key] > 0) {
          keptCounts[key] -= 1
          return false
        }
        return round.deletable
      })
      .map(round => round.id)
  }

  const saveDates = (dates, onSuccess) => {
    const massDates = [ ...dates ]
      .sort((a, b) => a - b)
      .map(d => moment(d).format('MM/DD/YYYY'))
      .join(',')
    const body = new URLSearchParams({
      'round[mass_dates]': massDates,
      'round[removed_round_ids]': computeRemovedRoundIds(dates).join(','),
      golfhub_scheduler: 'true',
    }).toString()

    setRequestPending(true)
    fetch(massCreateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': getAuthenticityToken(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    }).then(response => {
      if (!response.ok) {
        throw new Error(response.statusText)
      }
      return response.json()
    })
      .then(data => {
        if (!data.success) {
          setRequestPending(false)
          showErrorNotification(data.error || window.I18n.t('views.golfhub.request_failed'))
        } else if (data.async) {
          pollRoundsJob(data.job_id, onSuccess, Date.now())
        } else {
          setRequestPending(false)
          onSuccess()
        }
      })
      .catch(() => {
        setRequestPending(false)
        showErrorNotification(window.I18n.t('views.golfhub.request_failed'))
      })
  }

  // The directory preview only accepts rounds in the shape the server rendered
  // at init, so every change re-fetches the authoritative rounds instead of
  // rebuilding them from client-side dates.
  const applyServerRounds = (data) => {
    setConfirmedRounds(parseRounds(data.scheduler_rounds))
    if (!window.glg || !window.glg.golfhub) {
      return
    }
    window.glg.golfhub.refreshRoundsPreview({
      rounds: data.rounds,
      event_start_date: data.event_start_date,
      event_end_date: data.event_end_date,
    })
    if (window.glg.golfhub.updateEventDateValidation) {
      window.glg.golfhub.updateEventDateValidation(data.event_start_date)
    }
    if (window.glg.updateEventDates) {
      window.glg.updateEventDates(data.event_start_date, data.event_end_date)
    }
  }

  const refreshFromServer = () => {
    callAPI(getRoundsUrl, 'GET').then(data => {
      if (data.success) {
        applyServerRounds(data)
      }
    })
      .catch(() => {
        showErrorNotification(window.I18n.t('views.golfhub.request_failed'))
      })
  }

  const handleSave = (_formData, onClose) => {
    if (requestPending) {
      return
    }
    saveDates(details.rounds, () => {
      onClose()
      refreshFromServer()
    })
  }

  const handleDeleteChip = (chipIndex) => {
    if (requestPending) {
      return
    }
    const sorted = [ ...confirmedRounds ].sort((a, b) => a.date - b.date)
    const round = sorted[chipIndex]

    if (round.id) {
      // Removal runs in a background job - keep the widget pending, spin the
      // removed chip and poll the job before re-fetching the rounds
      setRequestPending(true)
      setRemovingRoundId(round.id)
      callAPI(removeRoundUrl, 'POST', {round_id: round.id}).then(data => {
        if (data.success) {
          pollRoundsJob(data.job_id, refreshFromServer, Date.now())
        } else {
          setRequestPending(false)
          setRemovingRoundId(null)
          showErrorNotification(data.error || window.I18n.t('views.golfhub.request_failed'))
        }
      })
        .catch(() => {
          setRequestPending(false)
          setRemovingRoundId(null)
          showErrorNotification(window.I18n.t('views.golfhub.request_failed'))
        })
    } else {
      const newDates = sorted.filter((_, i) => i !== chipIndex).map(r => r.date)
      saveDates(newDates, refreshFromServer)
    }
  }

  const lockedDates = confirmedRounds.filter(r => !r.deletable).map(r => r.date)

  const popupTitle = window.I18n.t(confirmedRounds.length > 0 ? 'shared.components.schedule_rounds.edit_rounds' : 'shared.components.schedule_rounds.add_rounds')
  const sortedRounds = [ ...confirmedRounds ].sort((a, b) => a.date - b.date)

  const deleteBlockedReason = (round) => {
    if (!round.deletable) {
      return window.I18n.t('shared.components.schedule_rounds.cannot_delete_scored_round')
    }
    if (confirmedRounds.length <= 1 && golfhubPageCreated) {
      return window.I18n.t('shared.components.schedule_rounds.at_least_one_round')
    }
    return null
  }

  return (
    <div className='round_scheduler_widget'>
      <div className='round_scheduler_header'>
        <div className='text-ds-form-label'>
          {window.I18n.t('shared.components.schedule_rounds.dates')}
          {dateRequired && <span className='color_red'> *</span>}
        </div>
        <div className='add-padding-left-8'>
          <GGButton
            icon='square-plus-o'
            variant='ghost'
            onClick={handleOpen}
          />
        </div>
      </div>

      {confirmedRounds.length > 0 && (
        <div className='round_date_chips_container'>
          {sortedRounds.map((round, idx) => {
            const isRemoving = removingRoundId && round.id === removingRoundId
            const blockedReason = deleteBlockedReason(round)
            const deleteControl = (
              <span className='round_date_chip_delete_wrapper'>
                <button type='button' className='round_date_chip_delete'
                        aria-label={window.I18n.t('buttons.delete')}
                        onClick={() => handleDeleteChip(idx)}
                        disabled={Boolean(blockedReason) || requestPending}>
                  {isRemoving ? <FaIcon icon='spinner' spin={true}/> : <FaIcon icon='xmark'/>}
                </button>
              </span>
            )
            return (
              <span key={round.id || +round.date}
                    className={`round_date_chip${isRemoving ? ' round_date_chip--removing' : ''}`}>
                <span className='round_date_chip_label'>
                  {formatDate(round.date, null, 'MMM DD')}
                </span>
                {blockedReason
                  ? <GlgTooltip tooltip={blockedReason}>{deleteControl}</GlgTooltip>
                  : deleteControl}
              </span>
            )
          })}
        </div>
      )}

      <GlgPopup
        title={popupTitle}
        show={showPopup}
        onClose={() => setShowPopup(false)}
        onSave={handleSave}
        modalClass='schedule_rounds_popup'
        manualClose={true}
        saveButtonEnabled={!requestPending && (details.rounds.length > 0 || !golfhubPageCreated)}
        isSaving={requestPending}
        saveButtonText={requestPending ? window.I18n.t('add_members.saving') : window.I18n.t('buttons.save')}
      >
        <ScheduleRounds
          showHeader={false}
          defaultSelectedButton={defaultRoundType}
          details={details}
          setDetails={setDetails}
          dayRoundNumber={dayRoundNumber}
          setDayRoundNumber={setDayRoundNumber}
          givenDays={givenDays}
          setGivenDays={setGivenDays}
          startDate={startDate}
          setStartDate={setStartDate}
          numberOfWeeks={numberOfWeeks}
          setNumberOfWeeks={setNumberOfWeeks}
          selectedButton={selectedButton}
          setSelectedButton={setSelectedButton}
          selectedWeekDays={selectedWeekDays}
          setSelectedWeekDays={setSelectedWeekDays}
          lockedDates={lockedDates}
          customerDateFormat={customerDateFormat}
        />
      </GlgPopup>
    </div>
  )
}

RoundSchedulerRoot.propTypes = {
  massCreateUrl: PropTypes.string.isRequired,
  removeRoundUrl: PropTypes.string.isRequired,
  getRoundsUrl: PropTypes.string.isRequired,
  existingRounds: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    deletable: PropTypes.bool.isRequired,
  })).isRequired,
  leagueProduct: PropTypes.string,
  golfhubPageCreated: PropTypes.bool,
  customerDateFormat: PropTypes.string,
  dateRequired: PropTypes.bool,
}

export default RoundSchedulerRoot
